import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { map } from 'rxjs/operators';
import { Observable, of, combineLatest } from 'rxjs';

import { finShare } from '@fcom/rx';
import { NotificationTheme } from '@fcom/ui-components';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { getPassengerTierLevel } from '@fcom/manage-booking/utils/service.utils';
import { groupBy, toLookup } from '@fcom/core/utils';

import {
  Category,
  FinnairCart,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairPassengerServiceItem,
  FinnairServiceItem,
  FinnairServiceSegmentItem,
} from '../../api/models';
import { FlightServices } from '../../interfaces';
import { getGroupedServices } from '../../utils/service-utils';

@Component({
  selector: 'fin-dapi-included-service',
  templateUrl: './included-service.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DapiIncludedServiceComponent implements OnInit {
  readonly Category = Category;
  readonly NotificationTheme = NotificationTheme;

  @Input() service: FinnairServiceItem;
  @Input() showAsPending = false;
  @Input() cartOrOrder$: Observable<FinnairCart | FinnairOrder>;
  @Input() passengers$: Observable<FinnairPassengerItem[]> = of([]);

  serviceSegmentItems$: Observable<FlightServices[]>;

  ngOnInit(): void {
    this.serviceSegmentItems$ = this.findServiceInAllFlights();
  }

  private findServiceInAllFlights(): Observable<FlightServices[]> {
    const isPassengerSelected = (
      selectedPassengers: FinnairPassengerItem[],
      passengerServiceItem: FinnairPassengerServiceItem
    ): boolean =>
      selectedPassengers.length === 0 || selectedPassengers.some(({ id }) => id === passengerServiceItem.id);

    return combineLatest([this.cartOrOrder$, this.passengers$]).pipe(
      map(([{ bounds }, selectedPassengers]) => {
        const allFlights = bounds.flatMap((bound) => bound.itinerary).filter(isFlight);
        const flightById = toLookup(allFlights, (flight) => flight.id);

        const allServiceSegments: FinnairServiceSegmentItem[] = this.service.bounds.flatMap(({ segments }) => segments);

        const serviceSegmentsByFlightId = groupBy(allServiceSegments, (segment) => segment.id);

        const serviceSegments = Object.entries(serviceSegmentsByFlightId)
          .map(([flightId, serviceSegmentsForFlight]) => {
            const flightPassengers = serviceSegmentsForFlight
              .flatMap(({ passengers }) => passengers)
              .filter((passenger) => isPassengerSelected(selectedPassengers, passenger))
              .map((passenger) => ({
                ...passenger,
                groupedServices: getGroupedServices(passenger.services, getPassengerTierLevel(passenger.id, selectedPassengers)),
              }));

            const serviceSegmentItem = serviceSegmentsForFlight[serviceSegmentsForFlight.length - 1];
            const flight = flightById[flightId];
            return {
              ...serviceSegmentItem,
              operatingAirline: flight.operatingAirline,
              passengers: flightPassengers,
            };
          })
          .filter((s) => s.passengers?.length > 0);

        // For Cancellation Cover display info only once for whole trip, not for each bound
        return this.service.category === Category.COVER ? serviceSegments.slice(0, 1) : serviceSegments;
      }),
      finShare()
    );
  }
}
